/**
 * Light/dark, remembered per browser.
 *
 * Defaults to whatever the OS prefers the first time the app is opened; after
 * that, whatever was picked here wins over the OS setting. There is no
 * per-user server setting for this — it lives in localStorage, like the PWA
 * install itself.
 */
import { useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'bjsfabric-theme'

// Keeps the browser's own chrome (status bar, task switcher card) matching
// the app instead of defaulting to the dark value baked into index.html.
const THEME_COLOR: Record<Theme, string> = {
  dark: '#0f1226',
  light: '#f5f6fb',
}

function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export function getInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'light' || stored === 'dark' ? stored : systemTheme()
}

/** Called once, synchronously, before the first render — see main.tsx. Also
 *  called by the hook below on every change, so the two never disagree. */
export function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme])
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    applyTheme(theme)
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  return [theme, setTheme] as const
}
